import { HttpClient } from '@angular/common/http';
import { DbContext, DbTable } from '../data/dbcontext.service';
import {
  SignificantPointGridModel,
  SignificantPointSummary,
  VoyageLogAdjustmentEditor,
  VoyageLogAdjustmentMetadata,
  VoyageLogCreationValidation,
  VoyageLogSummary
} from '../../models/voyage-log/voyagelog.models';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { DailyLogOperationInstance } from '../../models/projects/operation.models';
import { ActionType, ErrorType, PageValidationType, ValidationResult } from '../../models/projects/validation.models';
import { AppConfig } from '../../app/app.config';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs/Observable';
import { SetupMetadata } from '../../models/setup/setup.models';
import { VesselService } from '../vessel/vessel.service';

@Injectable()
export class VoyageLogService {
  config = AppConfig.settings;
  private readonly voyageLogTable: DbTable;

  constructor(
    private http: HttpClient,
    private dateParserService: DateParserService,
    private dbContext: DbContext,
    private timeZoneService: TimeZoneService,
    private vesselService: VesselService
  ) {
    this.voyageLogTable = this.dbContext.tables.find(t => t.name === 'VoyageLogData');
  }

  createVoyageLogSummary(operationInstance: DailyLogOperationInstance,
                         startLocationId: number,
                         latestLog: VoyageLogSummary
  ): VoyageLogSummary {
    const voyageLogSummary = new VoyageLogSummary();

    voyageLogSummary.startDate = cloneDeep(operationInstance.startTime.dateTime) as DateTime;
    voyageLogSummary.startLocationId = startLocationId;
    voyageLogSummary.voyageNumber = latestLog ? latestLog.voyageNumber + 1 : 1;
    voyageLogSummary.startOperationId = operationInstance.id ? operationInstance.id : operationInstance.localId;

    return voyageLogSummary;
  }

  saveVoyageLog(voyageLog: VoyageLogSummary): Promise<any> {
    const url = `${this.config.apiUrl}api/voyagelog/savevoyagelog`;
    return this.http.post<any>(url, voyageLog).toPromise();
  }

  saveLocalVoyageLog(voyageLog: VoyageLogSummary) {
    this.dbContext.addOrUpdate(this.voyageLogTable, voyageLog.id, voyageLog);
  }

  saveVoyageLogMultipleRecords(voyageLogData: VoyageLogSummary[]) {
    voyageLogData.forEach(record => {
      this.dbContext.addOrUpdate(this.voyageLogTable, record.id, record);
    });
  }

  async getVoyageLogLocalData(): Promise<VoyageLogSummary[]> {
    const data = await this.dbContext.getAll(this.voyageLogTable) as VoyageLogSummary[];
    return await this.deserializeVoyageLogData(data);
  }

  async deserializeVoyageLogData(data: VoyageLogSummary[]) {
    for (let i = 0; i < data.length; i++) {
      data[i].startDate = await this.dateParserService.deserializeDate(data[i].startDate, true, this.timeZoneService);
      data[i].endDate = await this.dateParserService.deserializeDate(data[i].endDate, true, this.timeZoneService);
    }

    return data;
  }

  async getVoyageLogForStartOperationId(operationInstanceId: number): Promise<VoyageLogSummary> {
    const url = `${this.config.apiUrl}api/voyagelog/getvoyagelogforstartoperation?operationInstanceId=${operationInstanceId}`;
    return this.http.get<VoyageLogSummary>(url).toPromise();
  }

  async getVoyageLogForEndOperationId(operationInstanceId: number): Promise<VoyageLogSummary> {
    const url = `${this.config.apiUrl}api/voyagelog/getvoyagelogforendoperation?operationInstanceId=${operationInstanceId}`;
    return this.http.get<VoyageLogSummary>(url).toPromise();
  }

  async getLatestVoyageLog(): Promise<VoyageLogSummary> {
    const url = `${this.config.apiUrl}api/voyagelog/getlatestvoyagelog`;
    const result = await this.http.get<VoyageLogSummary>(url).toPromise();

    return this.setVoyageLogDatesFromApi(result);
  }

  async getVoyageLogForTime(date: DateTime): Promise<VoyageLogSummary> {
    const encodedDate = encodeURIComponent(date.toISO());
    const url = `${this.config.apiUrl}api/voyagelog/getvoyagelogfortime?date=${encodedDate}`;
    const result = await this.http.get<VoyageLogSummary>(url).toPromise();

    return this.setVoyageLogDatesFromApi(result);
  }

  async getLatestVoyageLogBeforeTime(date: DateTime): Promise<VoyageLogSummary> {
    const encodedDate = encodeURIComponent(date.toISO());
    const url = `${this.config.apiUrl}api/voyagelog/getlatestvoyagelogbeforetime?date=${encodedDate}`;
    const result = await this.http.get<VoyageLogSummary>(url).toPromise();

    return this.setVoyageLogDatesFromApi(result);
  }

  async validateVoyageCreation(date: DateTime): Promise<VoyageLogCreationValidation> {
    const encodedDate = encodeURIComponent(date.toISO());
    const url = `${this.config.apiUrl}api/voyagelog/validatecreation?date=${encodedDate}`;

    return this.http
      .get<VoyageLogCreationValidation>(url)
      .toPromise();
  }

  // need to remove local offset after de-serialising dates from the api
  async setVoyageLogDatesFromApi(summary: VoyageLogSummary): Promise<VoyageLogSummary> {
    if (summary && summary.startDate) {
      summary.startDate = await this.dateParserService.deserializeDate(summary.startDate, true, this.timeZoneService);
    }

    if (summary && summary.endDate) {
      summary.endDate = await this.dateParserService.deserializeDate(summary.endDate, true, this.timeZoneService);
    }

    return summary;
  }

  async getAndUpdateVoyageLogData(): Promise<VoyageLogSummary[]> {
    const updatedData = await this.getUpdatedVoyageLogData();
    await this.syncVoyageLogData(updatedData);
    return this.getVoyageLogLocalData();
  }

  async validateVoyageLog(existingResult: ValidationResult, existingLogs?: VoyageLogSummary[]): Promise<ValidationResult> {
    if (await this.vesselService.isCurrentVesselNonJacking()) {
      return existingResult;
    }

    const voyageLogs = existingLogs && existingLogs.length > 0 ? existingLogs : await this.getVoyageLogLocalData();
    let startCargoInvalid = false;
    let endedVoyageNotComplete = false;

    for (let i = 0; i < voyageLogs.length; i++) {
      if (!startCargoInvalid && voyageLogs[i].startDate != null && voyageLogs[i].startCargoLoad === null) {
        startCargoInvalid = true;
      }

      if (!endedVoyageNotComplete && voyageLogs[i].endDate != null && !voyageLogs[i].completed) {
        endedVoyageNotComplete = true;
      }

      if (startCargoInvalid && endedVoyageNotComplete) {
        i = voyageLogs.length; // break out of the loop - no need to continue if both error cases are true
      }
    }

    if (startCargoInvalid) {
      existingResult.addError(
        'There is a started voyage with no "Start Cargo Load" recorded.',
        ErrorType.Warning,
        ActionType.VoyageLog,
        null,
        PageValidationType.VoyageLog
      );
    }

    if (endedVoyageNotComplete) {
      existingResult.addError(
        'There is an ended voyage which has not been completed.',
        ErrorType.Warning,
        ActionType.VoyageLog,
        null,
        PageValidationType.VoyageLog
      );
    }

    return existingResult;
  }

  async validateVoyageLogOnPage(voyageLogData: VoyageLogSummary[], existingResult: ValidationResult): Promise<ValidationResult> {
    if (await this.vesselService.isCurrentVesselNonJacking()) {
      return existingResult;
    }

    if (voyageLogData.some(e => e.startDate != null && e.startCargoLoad === null)) {
      existingResult.addError(
        'There is a started voyage with no "Start Cargo Load" recorded.',
        ErrorType.Warning,
        ActionType.VoyageLog,
        null,
        PageValidationType.VoyageLog
      );
    }

    if (voyageLogData.some(e => e.endCargoLoad < 0)) {
      existingResult.addError(
        'There is a negative "End Cargo Load".',
        ErrorType.Warning,
        ActionType.VoyageLog,
        null,
        PageValidationType.VoyageLog
      );
    }

    if (voyageLogData.some(e => e.endDate && (!e.distance || e.distance === 0))) {
      existingResult.addError(
        'There is an ended voyage with no Distance recorded.',
        ErrorType.Warning,
        ActionType.VoyageLog,
        null,
        PageValidationType.VoyageLog
      );
    }

    return existingResult;
  }

  public deleteAllVoyageLogData() {
    return this.dbContext.removeAll(this.voyageLogTable);
  }

  public getSignificantPointDataForLog(id: number): Observable<SignificantPointGridModel[]> {
    const url = `${this.config.apiUrl}api/voyagelog/${id}/significant-points`;
    return this.http.get<SignificantPointGridModel[]>(url);
  }

  async getAdjustmentMetadata(dailyLogDate: DateTime): Promise<VoyageLogAdjustmentMetadata> {
    const url = `${this.config.apiUrl}api/voyagelog/adjustment/${dailyLogDate.toMillis()}/metadata`;

    return this.http.get<VoyageLogAdjustmentMetadata>(url).toPromise()
      .then(metadata => {
        const result = Promise.resolve(metadata);
        return result;
      });
  }

  saveAdjustmentEditor(editor: VoyageLogAdjustmentEditor): Observable<VoyageLogSummary> {
    editor.date.dateTime = this.dateParserService.removeOffsetFromLuxonDate(editor.date.dateTime);

    const url = `${this.config.apiUrl}api/voyagelog/adjustment/save`;
    return this.http.post<VoyageLogSummary>(url, editor);
  }

  deleteAdjustmentEditor(id: number): Observable<VoyageLogSummary> {
    const url = `${this.config.apiUrl}api/voyagelog/adjustment/${id}/delete`;
    return this.http.delete<VoyageLogSummary>(url);
  }

  private async syncVoyageLogData(voyageLogData: VoyageLogSummary[]) {
    await this.deleteAllVoyageLogData();
    await this.saveVoyageLogMultipleRecords(voyageLogData);
  }

  private async getUpdatedVoyageLogData(): Promise<VoyageLogSummary[]> {
    const url = `${this.config.apiUrl}api/voyagelog/getvoyagelogdata`;
    return this.http.get<VoyageLogSummary[]>(url).toPromise();
  }

}



// WEBPACK FOOTER //
// ./src/services/voyage-log/voyage-log.service.ts